import { useCallback, useEffect, useRef } from 'react'
import { type Track } from './Video'
import { isIOS } from './isIOS'
import {
  SAFARI_DEFAULT_PICK_WINDOW_MS,
  activeTextTrackIndex,
  disableTextTracks,
  isIOSNativeFullscreen,
  toSourceTrackIndex,
} from './textTracks'
import { type Actions } from './videoReducer'

export const useIOSFullscreenTextTracks = ({
  videoRef,
  tracks,
  syncTextTracks,
  dispatch,
  sourceSrc,
}: {
  videoRef: React.RefObject<HTMLVideoElement | null>
  tracks: readonly Track[]
  syncTextTracks: (idx?: number) => void
  dispatch: React.Dispatch<Actions>
  sourceSrc: string | undefined
}) => {
  const tracksRef = useRef(tracks)
  tracksRef.current = tracks
  const syncTextTracksRef = useRef(syncTextTracks)
  syncTextTracksRef.current = syncTextTracks
  const defaultPickDeadlineRef = useRef(-Infinity)

  useEffect(() => {
    const video = videoRef.current
    if (!video || !isIOS()) return undefined

    const handleBeginFullscreen = () => {
      if (activeTextTrackIndex(video.textTracks) === -1) {
        defaultPickDeadlineRef.current =
          Date.now() + SAFARI_DEFAULT_PICK_WINDOW_MS
      }
    }
    // Safari stops drawing a `showing` track once its player closes, though
    // the mode still reads `showing`; switching it off first redraws it.
    const redrawCaptions = (idx: number) => {
      if (video.textTracks[idx]?.mode === 'showing') {
        video.textTracks[idx].mode = 'disabled'
      }
      syncTextTracksRef.current(idx)
    }
    const handleEndFullscreen = () =>
      redrawCaptions(
        toSourceTrackIndex(
          tracksRef.current,
          video.textTracks,
          activeTextTrackIndex(video.textTracks),
        ),
      )

    video.addEventListener('webkitbeginfullscreen', handleBeginFullscreen)
    video.addEventListener('webkitendfullscreen', handleEndFullscreen)
    return () => {
      video.removeEventListener('webkitbeginfullscreen', handleBeginFullscreen)
      video.removeEventListener('webkitendfullscreen', handleEndFullscreen)
    }
  }, [sourceSrc, videoRef])

  const handleIOSTextTracksChange = useCallback(
    (video: HTMLVideoElement) => {
      // Safari's player turns on the stream's DEFAULT=YES subtitles as it opens
      // while its menu reads Off. Only undo that when the page had captions off.
      const isSafariDefaultPick = () =>
        Date.now() <= defaultPickDeadlineRef.current

      const recordNativePlayerPick = ({ textTracks }: HTMLVideoElement) => {
        if (isSafariDefaultPick()) {
          disableTextTracks(textTracks)
          return
        }
        dispatch({
          type: 'trackChanged',
          payload: toSourceTrackIndex(
            tracksRef.current,
            textTracks,
            activeTextTrackIndex(textTracks),
          ),
        })
      }

      const adoptSourceTrack = ({ textTracks }: HTMLVideoElement) => {
        const activeIdx = activeTextTrackIndex(textTracks)
        const sourceIdx = toSourceTrackIndex(
          tracksRef.current,
          textTracks,
          activeIdx,
        )
        if (sourceIdx !== activeIdx) {
          syncTextTracksRef.current(sourceIdx)
          return
        }
        dispatch({ type: 'trackChanged', payload: sourceIdx })
      }

      return isIOSNativeFullscreen(video)
        ? recordNativePlayerPick(video)
        : adoptSourceTrack(video)
    },
    [dispatch],
  )

  return handleIOSTextTracksChange
}
